import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { MARK } from "./chartTheme";
import type { MetricLineChartProps } from "./contract";

/**
 * 수집 스냅샷의 다계열 추이 차트다.
 *
 * 다른 추이 차트와 다른 점은 두 가지다.
 *
 * 1. **결측을 연결하지 않는다**(`connectNulls={false}`). 수집 tick 이 빠진 구간은 값이 0 이 아니라
 *    지표가 없는 구간이다. 연결하면 그 구간이 "그동안 일정했다" 로 읽힌다. 선이 끊긴 자리가
 *    수집 공백이다.
 * 2. **계열마다 자기 `data` 를 가진다.** pod 별 계열은 500점 상한을 나눠 쓴다. 그래서 점 수와
 *    시각이 서로 다르다. 하나의 표로 합치려면 없는 자리를 채워야 하고, 이것은 1번을 어긴다.
 */
export function MetricLineChart({
  series,
  formatTick,
  formatValue,
  ariaLabel,
}: MetricLineChartProps) {
  return (
    <ResponsiveContainer width="100%" height={MARK.height}>
      {/* 위 여백은 최댓값 눈금 글자가 카드 경계에 닿지 않을 만큼 둔다. domain 이 auto 라서
          최댓값이 맨 위 눈금이 된다. */}
      <LineChart margin={{ top: 14, right: 8, bottom: 0, left: 0 }} aria-label={ariaLabel}>
        <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
        {/* 계열마다 시각이 달라서 x 는 category 가 아니라 수치 시간축이다. */}
        <XAxis
          dataKey="atMs"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          allowDuplicatedCategory={false}
          tickFormatter={(tick: number) => formatTick(tick)}
          tickLine={false}
          axisLine={false}
          stroke={CHART_CHROME.axis}
          fontSize={MARK.tickFontSize}
          minTickGap={32}
        />
        {/* 눈금도 지표 포맷터를 적용한다. 그래서 바이트·ms 원시값을 축에 그대로 표시하지 않는다.

            y 를 0 에 고정하지 않는다. 메모리처럼 300MB 대에서 변하는 지표를 0 기준으로 그리면
            두 선이 위쪽에 붙어 "어느 pod 에 누수가 있는가" 가 보이지 않는다. 0 을 빼면 작은 변화가
            과장되어 보일 수 있다. 이 위험은 축에 **절대값 눈금**을 그대로 표시해서 막는다. */}
        <YAxis
          domain={["auto", "auto"]}
          // `320 MB` 가 한 줄에 들어가는 폭이다. 56px 이면 단위가 다음 줄로 넘어간다.
          width={68}
          tickFormatter={(tick: number) => formatValue(tick)}
          tickLine={false}
          axisLine={false}
          stroke={CHART_CHROME.axis}
          fontSize={MARK.tickFontSize}
        />
        <Tooltip
          labelFormatter={(label: number) => formatTick(label)}
          formatter={(value: number, name: string) => [formatValue(value), name]}
          contentStyle={{
            background: CHART_CHROME.tooltipBg,
            border: `1px solid ${CHART_CHROME.tooltipBorder}`,
            color: CHART_CHROME.tooltipInk,
            fontSize: MARK.tickFontSize,
          }}
          itemStyle={{ color: CHART_CHROME.tooltipInk }}
        />
        {series.length > 1 ? <Legend iconType="plainline" /> : null}
        {series.map((one) => (
          <Line
            key={one.key}
            data={[...one.points]}
            name={one.label}
            dataKey="value"
            type="linear"
            stroke={one.color}
            strokeWidth={MARK.strokeWidth}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
